import { useEffect, useState, useRef } from 'react'

import {
	useHandleNewSurveySubmissions,
	useNewSetNextButtonDisabled,
	useNewSurveyState,
	useNewSurveySubmissions
} from '@context/new-survey/useNewSurvey'

import type { SurveyQuestion } from '@lib/types/subscription-box'

type MultiSelectParams = {
	surveyQuestion: SurveyQuestion
	isRequired?: boolean
	index: number
}
export default function MultiSelect({ surveyQuestion, isRequired, index }: MultiSelectParams) {
	const { currentIndex } = useNewSurveyState()
	const handleSurveySubmissions = useHandleNewSurveySubmissions()
	const setNextButtonDisabled = useNewSetNextButtonDisabled()
	const surveySubmissions = useNewSurveySubmissions()

	// Track if user has interacted with this component to prevent
	// overriding their selections with synchronized data
	const userInteractedRef = useRef<boolean>(false)
	// Track previous submission state to avoid unnecessary updates
	const prevSubmissionRef = useRef<string[]>([])

	const [answerOptions, setAnswerOptions] = useState(
		surveyQuestion?.surveyAnswerOptions?.map(({ optionValue, optionLabel, id }) => ({
			optionValue,
			optionLabel,
			active: false,
			id
		}))
	)

	// Handle state change on option click
	const handleValueChange = (optionValue: string) => {
		// Mark that user has interacted with this component
		userInteractedRef.current = true

		setAnswerOptions((prev) => {
			const updated = prev.map(({ optionValue: prevOptionValue, active: prevActive, ...rest }) => ({
				optionValue: prevOptionValue,
				active: optionValue === prevOptionValue ? !prevActive : prevActive,
				...rest
			}))
			return updated
		})
	}

	// Sync with external survey submissions - only runs before user interaction
	useEffect(() => {
		if (userInteractedRef.current) return

		const submission = surveySubmissions.find((sub) => sub.questionId === surveyQuestion.id)
		if (!submission?.response?.length) return

		// Use JSON.stringify for simpler deep comparison
		const responseJSON = JSON.stringify([...submission.response].sort())
		const prevResponseJSON = JSON.stringify([...prevSubmissionRef.current].sort())

		if (responseJSON !== prevResponseJSON) {
			// Update local state to match submission
			setAnswerOptions((prev) =>
				prev.map((option) => ({
					...option,
					active: submission.response.includes(option.optionValue)
				}))
			)

			// Store current submission to prevent future unnecessary updates
			prevSubmissionRef.current = [...submission.response]
		}
	}, [surveySubmissions, surveyQuestion.id])

	// Update survey context when options change
	useEffect(() => {
		if (index === currentIndex) {
			handleSurveySubmissions(surveyQuestion, answerOptions)
		}
	}, [answerOptions, index, currentIndex, handleSurveySubmissions, surveyQuestion])

	// Handle disabling next button based on selection requirements
	useEffect(() => {
		if (index !== currentIndex) return

		const selectedOptions = answerOptions?.filter(({ active }) => active)
		const minRequired = surveyQuestion?.displayOptions?.minNumSelected

		// If question has a minimum selection requirement
		if (minRequired) {
			setNextButtonDisabled(selectedOptions.length < minRequired)
			return
		}

		// If required but nothing selected
		if (isRequired && selectedOptions.length === 0) {
			setNextButtonDisabled(true)
			return
		}

		// Default to enabled
		setNextButtonDisabled(false)
	}, [answerOptions, surveyQuestion, index, currentIndex, setNextButtonDisabled, isRequired])

	if (index !== currentIndex) return null

	return (
		<div className="flex flex-col font-sans">
			{/* Survey question body */}
			{surveyQuestion?.body && (
				<h3 className="text-2xl md:text-3xl font-medium text-center">{surveyQuestion.body}</h3>
			)}

			{/* Subtitle */}
			{surveyQuestion?.displayOptions?.subtitle && (
				<p className="mt-2 text-sm md:text-base text-center text-gray-500 font-normal">
					{surveyQuestion.displayOptions.subtitle}
				</p>
			)}

			{/* Modal description */}
			{surveyQuestion?.displayOptions?.minNumSelected && (
				<p className="mt-4 font-normal text-center md:mt-16 text-gray-500 max-md:text-sm">
					Choose at least {surveyQuestion?.displayOptions?.minNumSelected}
				</p>
			)}

			{Array.isArray(surveyQuestion?.surveyAnswerOptions) && (
				<ul className="flex flex-col md:flex-row md:flex-wrap md:justify-center w-full max-w-[500px] md:max-w-none mx-auto gap-3 md:gap-4 self-center mt-8 md:mt-12">
					{answerOptions?.map(({ optionLabel, optionValue, active, id }) =>
						optionLabel ? (
							<li className="w-full md:w-auto" key={id}>
								<button
									type="button"
									className={`w-full px-6 py-4 font-normal border rounded-lg transition-colors duration-200 ease-in-out text-base md:text-lg ${
										active
											? 'border-bbx-spark bg-bbx-spark/10 text-bbx-style'
											: 'border-gray-300 bg-white text-gray-600'
									}`}
									onClick={() => handleValueChange(optionValue)}
								>
									{optionLabel}
								</button>
							</li>
						) : null
					)}
				</ul>
			)}
		</div>
	)
}
